import { useFormContext } from "react-hook-form";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { FieldProps } from ".";
import { format, Locale, parseISO } from "date-fns";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { TimeValidationError } from "@mui/x-date-pickers";
import { PickerChangeHandlerContext } from "@mui/x-date-pickers/internals/hooks/usePicker/usePickerValue.types";
import { Domains } from "@/core/types/types";
import {
  getTopLevelDomain,
  getDatePickerLocaleByTLD,
  getTranslationTexts,
} from "@/core/utils";
import { useEffect } from "react";
import getFlatFormValue from "@/core/utils/getFlatFormValue";
import { timeFormats } from "@/core/data";
import { HOTJAR_ATTRIBUTES as extraProps } from "@/core/data";
import { TimeField as ReusableTimeField } from "@flightright/reusable-components";

const TLD: Domains | string = getTopLevelDomain() || Domains.NET;
const ADOPTER_LOCALE: Locale = getDatePickerLocaleByTLD(TLD as Domains);
const TEXT: object = getTranslationTexts(TLD);

interface TimeFieldProps extends FieldProps {
  initialValue?: string | null;
  displayError?: boolean;
}

const TimeField = ({
  field,
  initialValue,
  displayError = true,
}: TimeFieldProps) => {
  const {
    control,
    clearErrors,
    setValue,
    formState: { errors },
    watch,
  } = useFormContext();
  const formValues = watch();
  const initialValueIsDateTime: boolean = initialValue?.includes("T") ?? false;
  const selectedFlightDate: string | null | undefined = initialValueIsDateTime
    ? initialValue?.split("T")[0]
    : initialValue;
  const defaultValue = initialValue ? parseISO(initialValue) : null;
  const formStateError: boolean = !!errors[field.key];
  const formStateErrorMessage: string =
    errors[field.key]?.message?.toString() || "";
  const timeFormat: string = timeFormats[TLD];
  const errorTranslationKey: string = field.properties?.errorTranslationKey
    ? field.properties?.errorTranslationKey
    : field.key;
  const shouldDisplayError = displayError
    ? field.validate?.required ?? false
    : false;

  useEffect(() => {
    // handling of nested array fields
    const timeValue = getFlatFormValue(formValues, field.key);
    if (initialValue && initialValueIsDateTime) {
      setValue(field.key, initialValue);
      clearErrors(field.key);
    } else if (selectedFlightDate && timeValue) {
      handleChange(timeValue, { validationError: null });
    }
  }, [selectedFlightDate]);

  const handleChange = (
    time: string | number | Date | null,
    { validationError }: PickerChangeHandlerContext<TimeValidationError>
  ) => {
    if (time && !validationError) {
      const formattedTime: string = format(new Date(time), "HH:mm:ss");
      const timeValue: string = !!selectedFlightDate
        ? `${selectedFlightDate}T${formattedTime}`
        : `${formattedTime}`;
      setValue(field.key, timeValue);
      clearErrors(field.key);
    } else {
      setValue(field.key, null);
    }
  };

  return (
    <ReusableTimeField
      dateAdapter={AdapterDateFns}
      adapterLocale={ADOPTER_LOCALE}
      localeText={TEXT}
      id={field.key}
      name={field.key}
      control={control}
      rules={{
        required: {
          value: field.validate?.required || false,
          message: t(`validate.${errorTranslationKey}.required.message`),
        },
      }}
      defaultValue={defaultValue}
      label={t(field.label)}
      handleChange={handleChange}
      timeFormat={timeFormat}
      error={shouldDisplayError ? formStateError : undefined}
      errorMessage={shouldDisplayError ? formStateErrorMessage : undefined}
      {...extraProps}
    />
  );
};

export default TimeField;
